import { FC } from 'react';
import { useSelector } from 'react-redux';
import { useGetUserProfilePicture } from '@queries/platform/aws';
import { selectUserNickname, selectUserUuid } from '@redux/slices/userSlice';
import {
    CustomEditorWrapper,
    EditorTheme,
    FooterVariant,
    HeaderVariant,
    Button,
    Icon,
    CodeSection,
    Tooltip
} from 'learning-panel-components';
import { translate } from 'translations';
import TopBar from '../topBar/topBar';
import { userQuestion } from '../previewPage/previewPage';
import VulnerabilityInfo, { RelatedContent } from '../vulnerabilityInfo/vulnerabilityInfo';
import styles from './game.module.scss';

const Game: FC<GameProps> = (props: GameProps) => {
    const {
        name,
        vulName,
        questionNumber,
        totalQuestions,
        onNavigateToNextQuestion,
        onNavigateToPreviousQuestion,
        potentialScore,
        userScore,
        userQuestions,
        links,
        relatedContent,
        rankNumber,
        rankLevel,
        vulDescription,
        onNavigateToQuestion,
        code = '',
        outOfTries,
        onMainAction,
        onSecondaryAction,
        footerVariant,
        onCodeChange,
        remediationCode = '',
        lastRemediationAttempt,
        percentage,
        codeSections,
        attemptsLeftNumber,
        completedMask,
        language,
        isGameDone
    } = props;

    const userNickname = useSelector(selectUserNickname);
    const userUuid = useSelector(selectUserUuid);
    const { data: userProfilePicture } = useGetUserProfilePicture(userUuid);

    const RESTORE_PREVIOUS = translate('RestorePrevious');
    const PASTE_CODE = translate('PasteCode');

    const remediationReadOnly =
        [FooterVariant.DONE, FooterVariant.END_GAME, FooterVariant.RESULT, FooterVariant.LOADING].includes(
            footerVariant
        ) || outOfTries;

    const restorePreviousAction = () => {
        if (!lastRemediationAttempt || remediationReadOnly) return null;

        return (
            <Button
                onClick={() => onCodeChange(lastRemediationAttempt)}
                startIcon={<Icon name="revertGray" style={{ display: 'flex' }} />}
                type={'learningPanelFlatGray'}
                lowerCase={true}
                text={RESTORE_PREVIOUS}
            />
        );
    };

    const pasteCodeAction = () => {
        if (remediationReadOnly) return null;

        return (
            <Button
                onClick={() => onCodeChange(code)}
                startIcon={<Icon name="arrowBack" style={{ display: 'flex' }} />}
                type={'learningPanelFlatGray'}
                lowerCase={true}
                text={PASTE_CODE}
            />
        );
    };

    return (
        <div className={styles['lab-game']}>
            <TopBar
                name={name}
                vulName={vulName}
                questionNumber={questionNumber}
                onNavigateToNextQuestion={onNavigateToNextQuestion}
                onNavigateToPreviousQuestion={onNavigateToPreviousQuestion}
                potentialScore={potentialScore}
                userScore={userScore}
                totalQuestions={totalQuestions}
                userQuestions={userQuestions}
                userNickname={userNickname}
                userAvatar={userProfilePicture?.url}
                onNavigateToQuestion={onNavigateToQuestion}
            />
            <div className={styles['lab-content']}>
                <VulnerabilityInfo
                    rankNumber={rankNumber}
                    rankLevel={rankLevel}
                    links={links}
                    relatedContent={relatedContent}
                    name={vulName}
                    description={vulDescription}
                />

                <div className={styles['game-container']}>
                    <div className={styles['game-header']}>
                        <div className={styles['game-header-title']}>
                            <div className={styles.bold}>{translate('SaveYourCode')}</div>
                            <div>{translate('There’sAVulnerabilityInThisCode')}</div>
                            <div className={styles.panel}>{translate('RemediationPanel')}</div>
                            <div>{translate('toTypeYourSolution')}</div>
                        </div>
                        <div className={styles.attempts}>
                            <Tooltip title={translate('AttemptsTooltip')} placement="top-start">
                                <span>
                                    <Icon name="infoBlue" className={styles.info} />
                                </span>
                            </Tooltip>
                            <div>{translate('Attempts')}</div>
                            <div className={styles.numbers}>{`${attemptsLeftNumber}/3`}</div>
                            <div className={styles.shilds}>
                                {[...Array(3)].map((_, index) => (
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/${
                                            index < 3 - attemptsLeftNumber ? 'killedAttempt.svg' : 'liveAttempt.svg'
                                        }`}
                                        alt="shild"
                                        key={index}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={styles['ides-container']}>
                        <CustomEditorWrapper
                            code={code}
                            onCodeChange={undefined}
                            readOnly={true}
                            codeSections={[]}
                            theme={EditorTheme.VSDARK}
                            language={language}
                            includeActions={false}
                            mainAction={undefined}
                            secondaryAction={undefined}
                            id={HeaderVariant.VULNERABLE}
                            headerVariant={HeaderVariant.VULNERABLE}
                            userLogo={''}
                            completedMask={false}
                            percentage={0}
                            outOfTries={false}
                            headerActions={pasteCodeAction}
                            userNickname={userNickname}
                        />
                        <CustomEditorWrapper
                            code={remediationCode}
                            onCodeChange={onCodeChange}
                            readOnly={remediationReadOnly}
                            theme={EditorTheme.VSDARK}
                            language={language}
                            includeActions={true}
                            mainAction={onMainAction}
                            secondaryAction={onSecondaryAction}
                            id={HeaderVariant.REMEDIATION}
                            headerVariant={HeaderVariant.REMEDIATION}
                            footerVariant={footerVariant}
                            userLogo={userProfilePicture?.url}
                            completedMask={completedMask}
                            percentage={percentage}
                            outOfTries={outOfTries}
                            headerActions={restorePreviousAction}
                            codeSections={codeSections}
                            userNickname={userNickname}
                            isGameDone={isGameDone}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

interface GameProps {
    name: string;
    vulName: string;
    vulDescription: string;
    questionNumber: number;
    totalQuestions: number;
    onNavigateToNextQuestion: (questionNumber: number) => void;
    onNavigateToPreviousQuestion: (questionNumber: number) => void;
    potentialScore: number;
    userScore: number;
    userQuestions: userQuestion[];
    rankNumber: string;
    rankLevel: 'medium' | 'high' | 'low';
    links: { tag: string; url: string }[];
    relatedContent: RelatedContent;
    onNavigateToQuestion: Function;
    code?: string;
    footerVariant: FooterVariant;
    outOfTries: boolean;
    onCodeChange: (code: string) => void;
    remediationCode?: string;
    onMainAction: () => void;
    onSecondaryAction: () => void;
    lastRemediationAttempt: string | undefined;
    percentage: number;
    codeSections: CodeSection[];
    attemptsLeftNumber: number;
    completedMask: boolean;
    language: string;
    isGameDone: boolean;
}

export default Game;
